import { adminUserContextService } from "@/services/api/admin-user-context-service";
import { useAdminUserContextStore } from "@/stores/admin-user-context-store";
import { useIsAuthenticated } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

/**
 * Fetches the current admin user's context (identity + permissions) and keeps
 * the sync `useAdminUserContextStore` in sync with the latest server state.
 *
 * Use this hook at the root of the authenticated tree to ensure context is
 * loaded before any component or route guard tries to read it. For
 * permission checks within components, prefer the helper functions exported
 * from `admin-user-context-store.ts` (sync, no need to call the hook).
 *
 * Caching:
 *   - 5 minute staleTime mirrors the client app's user-context behavior.
 *   - Refetches on window focus by default (TanStack Query) — useful for
 *     picking up permission changes without a full sign-out.
 */
export function useAdminUserContext() {
	const isAuthenticated = useIsAuthenticated();
	const { setContext, clear } = useAdminUserContextStore();

	const query = useQuery({
		queryKey: ["admin-user-context"],
		queryFn: () => adminUserContextService.getCurrent(),
		enabled: isAuthenticated,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
		retry: 1,
	});

	// Keep the sync store aligned with the latest fetch.
	useEffect(() => {
		if (query.data) {
			setContext(query.data);
		}
	}, [query.data, setContext]);

	// Clear the store when the user signs out (MSAL auth flips false).
	useEffect(() => {
		if (!isAuthenticated) {
			clear();
		}
	}, [isAuthenticated, clear]);

	return query;
}
